import { drawDrawingComposite } from './drawingShadow';
import { useEffect, useRef, type RefObject } from 'react';
import type { Draft } from '../shared/types';
import type { DrawingState } from './Drawing';
import { api, placementKey } from './api';

/** One encoded temporary frame; lease renewals do not rerender unchanged pixels. */
export class WallPreviewFrame {
  private current:{canvas:HTMLCanvasElement;width:number;height:number;revision:number;shadow:HTMLCanvasElement|undefined;shadowWidth:number;shadowHeight:number;effects:string;image:string}|null=null;
  constructor(private createCanvas:()=>HTMLCanvasElement=()=>document.createElement('canvas')) {}
  clear(){this.current=null;}
  encode(state:DrawingState,revision:number,effects='') {
    const {canvas,shadow}=state,current=this.current;
    if(current&&current.canvas===canvas&&current.width===canvas.width&&current.height===canvas.height&&current.revision===revision&&current.shadow===shadow&&current.shadowWidth===(shadow?.width??0)&&current.shadowHeight===(shadow?.height??0)&&current.effects===effects)return current;
    const preview=this.createCanvas(),scale=Math.min(1,128/Math.max(canvas.width,canvas.height));
    preview.width=Math.max(1,Math.round(canvas.width*scale));preview.height=Math.max(1,Math.round(canvas.height*scale));
    try{
      const context=preview.getContext('2d');if(!context)return null;
      drawDrawingComposite(context,canvas,shadow,preview.width,preview.height);
      return this.current={canvas,width:canvas.width,height:canvas.height,revision,shadow,shadowWidth:shadow?.width??0,shadowHeight:shadow?.height??0,effects,image:preview.toDataURL('image/png')};
    }finally{preview.width=0;preview.height=0;}
  }
}

// Publishing is best-effort; the editable canvas and final confirmation are independent.
export function useWallPreview(draft:Draft|null, drawing:DrawingState|null, connected:boolean,worldEpoch=0,liveDrawing?:RefObject<DrawingState|null>,effects='') {
  const latest=useRef(drawing); latest.current=drawing;
  const latestEffects=useRef(effects);latestEffects.current=effects;
  const version=useRef(0),lastState=useRef(drawing);
  if(lastState.current!==drawing){version.current++;lastState.current=drawing;}
  useEffect(()=>{
    if(!draft||!connected)return;
    let id=placementKey(),revision=0,sentFrame:ReturnType<WallPreviewFrame['encode']>=null,sentAt=0,published=false,ended=false;
    const encoder=new WallPreviewFrame();
    let pending:AbortController|null=null;
    const end=()=>{pending?.abort();pending=null;void api.endPreview(id).catch(()=>{});};
    const publish=async()=>{
      if(ended||pending)return;
      const state=liveDrawing?liveDrawing.current:latest.current;
      if(!state?.drawn){encoder.clear();if(published){end();id=placementKey();revision=0;published=false;sentFrame=null;}return;}
      const currentVersion=liveDrawing?state.pixelRevision:version.current;
      const controller=new AbortController();pending=controller;
      try{
        const frame=encoder.encode(state,currentVersion,latestEffects.current);
        if(!frame||sentFrame===frame&&Date.now()-sentAt<3000)return;
        await api.preview({worldEpoch,id,revision:++revision,surfaceId:draft.surfaceId,x:draft.x,y:draft.y,width:draft.width,height:draft.height,paintOrder:draft.paintOrder??'over',...(draft.belowPlacementId?{belowPlacementId:draft.belowPlacementId}:{}),image:frame.image},controller.signal);
        if(!ended){published=true;sentFrame=frame;sentAt=Date.now();}
      }catch{ /* A missed temporary preview never prevents editing or confirmation. */ }
      finally{if(pending===controller)pending=null;}
    };
    const timer=setInterval(()=>void publish(),750);
    void publish();
    return()=>{ended=true;clearInterval(timer);end();};
  },[draft?.surfaceId,draft?.x,draft?.y,draft?.width,draft?.height,draft?.paintOrder,draft?.belowPlacementId,connected,worldEpoch]);
}
